import * as React from "react";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {PermissionsInfo} from "@khan/shared-core";

import {PermissionProviderContext} from "./permission-provider-context.ts";
import {PERMISSION_QUERY} from "./permission-query.ts";
import type {Permission} from "./permission-types.ts";
import type {PermissionProviderResultProps} from "./types.ts";

type Props = Readonly<{
    // The permission to check.
    permission: Permission;
}>;

/**
 * Provides access to a user's detailed permissions in an SSR-safe way.
 *
 * Example usage:
 * ```tsx
 * const result = usePermission({permission: "can_publish"});
 * if (result.loading) {
 *   return <Spinner />;
 * } else if (result.error) {
 *   return <ErrorMessage />;
 * } else if (result.hasPermission) {
 *   return ... // display something for a user who has permission
 * } else {
 *   return ... // display something for a user who DOESN'T have permission
 * }
 * ```
 */
export const usePermission = ({
    permission,
}: Props): PermissionProviderResultProps => {
    // Check if we have a context override for this permission request.
    const context = React.useContext(PermissionProviderContext);
    const mockedResult = context?.hasPermission(permission);

    const [result] = useQuery(PERMISSION_QUERY, {
        variables: {
            name: permission.capability,
            scope: "scope" in permission ? permission.scope : undefined,
        },
        fetchPolicy: FetchPolicy.CacheBeforeNetwork,
        skip: !PermissionsInfo.hasAnyPermissions || mockedResult != null,
    });

    return React.useMemo(() => {
        if (mockedResult != null) {
            return mockedResult;
        }

        return {
            loading: result.status === "loading",
            error: result.status === "error" ? result.error : undefined,
            hasPermission:
                (result.status === "success"
                    ? result.data?.user?.hasPermission
                    : undefined) ??
                (result.status !== "loading" && result.status !== "error"
                    ? false
                    : undefined),
        };
    }, [mockedResult, result]);
};
